import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../utils/constant';
import Swal from 'sweetalert2';
import { Layers, RefreshCw, CheckCircle, AlertTriangle, ArrowRight, Package, Box } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ArrangeDuplicatesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [data, setData] = useState({
    finalDuplicates: [],
    rawDuplicates: [],
    totalFinalDuplicateGroups: 0,
    totalRawDuplicateGroups: 0
  });

  const getCompanyId = () => {
    const company = JSON.parse(localStorage.getItem('company'));
    return company?.company?.id;
  };

  const fetchDuplicates = async () => {
    const companyId = getCompanyId();
    if (!companyId) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/final-products/duplicates/analyze/${companyId}`);
      if (response.data?.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching duplicates:', error);
      Swal.fire('Error', 'Failed to fetch duplicate products analysis.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const handleArrangeDuplicates = async () => {
    const companyId = getCompanyId();
    if (!companyId) return;

    const result = await Swal.fire({
      title: 'Merge Duplicate Products?',
      text: `This will group all duplicate product names with the same brand into a single product ID and re-link all variations and stock to that single product. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#00b894',
      cancelButtonColor: '#d63031',
      confirmButtonText: 'Yes, Arrange & Merge!'
    });

    if (!result.isConfirmed) return;

    setMerging(true);
    try {
      const response = await axios.post(`${API_URL}/final-products/duplicates/arrange/${companyId}`);
      if (response.data?.success) {
        const stats = response.data.stats || {};
        Swal.fire({
          icon: 'success',
          title: 'Duplicates Merged Successfully!',
          html: `
            <div class="text-start">
              <p><strong>Final Products Merged:</strong> ${stats.finalMergedProductsCount || 0} (across ${stats.finalMergedGroups || 0} groups)</p>
              <p><strong>Stock Variations Re-linked:</strong> ${stats.finalVariationsUpdated || 0}</p>
              <p><strong>Available Variants Re-linked:</strong> ${stats.availableVariantsUpdated || 0}</p>
              <p><strong>Raw Products Merged:</strong> ${stats.rawMergedProductsCount || 0}</p>
            </div>
          `
        });
        fetchDuplicates();
      } else {
        Swal.fire('Error', response.data?.message || 'Merge failed', 'error');
      }
    } catch (error) {
      console.error('Error arranging duplicates:', error);
      Swal.fire('Error', error.response?.data?.message || error.message || 'Failed to arrange duplicates', 'error');
    } finally {
      setMerging(false);
    }
  };

  const totalDuplicateGroups = data.totalFinalDuplicateGroups + data.totalRawDuplicateGroups;

  return (
    <div className="page-wrapper p-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div className="content container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-3 shadow-sm border">
          <div>
            <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <Layers className="text-primary" size={24} />
              Arrange Duplicate Products & Variations
            </h4>
            <p className="text-muted small mb-0">
              Consolidate products sharing the same name & brand into a single Master Product ID and update all variation pointers.
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary d-flex align-items-center gap-1"
              onClick={fetchDuplicates}
              disabled={loading || merging}
            >
              <RefreshCw className={loading ? 'spin' : ''} size={16} />
              Scan Again
            </button>
            <button
              className="btn btn-success d-flex align-items-center gap-1 px-3 fw-bold"
              onClick={handleArrangeDuplicates}
              disabled={loading || merging || totalDuplicateGroups === 0}
              style={{ backgroundColor: '#00b894', borderColor: '#00b894' }}
            >
              <CheckCircle size={18} />
              {merging ? 'Arranging...' : 'Arrange & Merge Duplicates'}
            </button>
          </div>
        </div>

        {/* Metrics Overview Cards */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm rounded-3 p-3 bg-gradient text-white" style={{ background: 'linear-gradient(135deg, #0984e3, #6c5ce7)' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-white-50 small fw-semibold">Total Duplicate Groups</div>
                  <h2 className="fw-bold mb-0 mt-1">{totalDuplicateGroups}</h2>
                </div>
                <Layers size={36} className="text-white-50" />
              </div>
            </div>
          </div>

          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm rounded-3 p-3 bg-white border-start border-4 border-primary">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small fw-semibold">Final Product Groups</div>
                  <h2 className="fw-bold mb-0 mt-1 text-primary">{data.totalFinalDuplicateGroups}</h2>
                </div>
                <Package size={32} className="text-primary opacity-50" />
              </div>
            </div>
          </div>

          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm rounded-3 p-3 bg-white border-start border-4 border-warning">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small fw-semibold">Raw Product Groups</div>
                  <h2 className="fw-bold mb-0 mt-1 text-warning">{data.totalRawDuplicateGroups}</h2>
                </div>
                <Box size={32} className="text-warning opacity-50" />
              </div>
            </div>
          </div>

          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm rounded-3 p-3 bg-white border-start border-4 border-success">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small fw-semibold">System Status</div>
                  <h6 className={`fw-bold mb-0 mt-2 ${totalDuplicateGroups > 0 ? 'text-danger' : 'text-success'}`}>
                    {totalDuplicateGroups > 0 ? `${totalDuplicateGroups} Duplicates Detected` : 'All Products Clean & Unique'}
                  </h6>
                </div>
                {totalDuplicateGroups > 0 ? (
                  <AlertTriangle size={32} className="text-danger opacity-75" />
                ) : (
                  <CheckCircle size={32} className="text-success opacity-75" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Final Products Duplicates Table */}
        <div className="card border-0 shadow-sm rounded-3 mb-4">
          <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
            <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
              <Package size={18} className="text-primary" />
              Final Products Duplicate Groups ({data.finalDuplicates.length})
            </h6>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2 text-muted">Scanning products database...</p>
              </div>
            ) : data.finalDuplicates.length === 0 ? (
              <div className="text-center py-5 text-success">
                <CheckCircle size={48} className="mb-2 text-success opacity-75" />
                <h6 className="fw-bold mb-1">No Duplicate Final Products Found!</h6>
                <p className="text-muted small">All final product names under their respective brands are unique.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Product Name</th>
                      <th>Brand</th>
                      <th>Master Product ID (Retained)</th>
                      <th>Duplicate IDs (To be Merged)</th>
                      <th>Total Stock Variations</th>
                      <th>Total Available Variants</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.finalDuplicates.map((group, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td className="fw-bold text-dark">{group.name}</td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {group.brand_name}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-success text-white px-2 py-1 fs-13">
                            ID: {group.primary_id}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {group.duplicate_ids.map(id => (
                              <span key={id} className="badge bg-danger text-white">
                                ID: {id}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-primary rounded-pill px-2">
                            {group.total_variations_count} variations
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info text-white rounded-pill px-2">
                            {group.total_available_variants_count} variants
                          </span>
                        </td>
                        <td>
                          <span className="text-muted small d-flex align-items-center gap-1">
                            Pending Merge <ArrowRight size={14} />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Raw Products Duplicates Table */}
        {data.rawDuplicates.length > 0 && (
          <div className="card border-0 shadow-sm rounded-3">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
              <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                <Box size={18} className="text-warning" />
                Raw Products Duplicate Groups ({data.rawDuplicates.length})
              </h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Product Name</th>
                      <th>Brand</th>
                      <th>Master Raw Product ID</th>
                      <th>Duplicate Raw IDs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rawDuplicates.map((group, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td className="fw-bold text-dark">{group.name}</td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {group.brand_name}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-success text-white px-2 py-1">
                            ID: {group.primary_id}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {group.duplicate_ids.map(id => (
                              <span key={id} className="badge bg-danger text-white">
                                ID: {id}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArrangeDuplicatesPage;
